import React, { useEffect, useRef, useState } from 'react';
import { Customer } from './components/CustomerCard';
import { Task } from './components/TaskCard';
import { Invoice } from './components/InvoiceCard';
import { Payout } from './components/PayoutCard';
import { Earning } from './components/EarningCard';
import { AddCustomerModal } from './components/AddCustomerModal';
import { EditCustomerModal } from './components/EditCustomerModal';
import { Dashboard } from './pages/Dashboard';
import { CustomerManagement } from './pages/CustomerManagement';
import { CustomerDetail } from './pages/CustomerDetail';
import { Invoices } from './pages/Invoices';
import { Payouts } from './pages/Payouts';
import { Login } from './components/Login';
import { Profile } from './pages/Profile';
import { Users, UserRole } from './pages/Users';
import { Calendar } from './pages/Calendar';
import { Report } from './pages/Report';
import { ChargerSettings } from './pages/ChargerSettings';
import { useFirestoreCollection } from './hooks/useFirestore';
import { LandingPage } from './pages/LandingPage';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { Solar } from './pages/Solar';
import { Regelgrundlag } from './pages/Regelgrundlag';
import { ReportData } from './components/AddReportModal';
import { TaskDetail } from './pages/TaskDetail';
import { AddTaskPage } from './pages/AddTaskPage';
import { ManageTasksPage } from './pages/ManageTasksPage';
import { Sidebar } from './components/Sidebar';
import { View } from './types/view';
import { QualityAssurance } from './pages/QualityAssurance';
import { QualityReportDetail } from './pages/QualityReportDetail';
import { qualityReports as initialQualityReports } from './data/qualityReports';
import { QualityReport } from './types/quality';
import { company } from './data/company';
import { MesterAI } from './pages/MesterAI';
import { TopBar } from './components/TopBar';
import { BottomNav } from './components/BottomNav';
import { SearchResult } from './utils/search';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState<View>('landing');
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('Medarbejder');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [previousView, setPreviousView] = useState<View>('tasks');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  const [initialCustomerIdForTask, setInitialCustomerIdForTask] = useState<string | undefined>(undefined);
  const [selectedQualityReportId, setSelectedQualityReportId] = useState<string | null>(null);
  const hasSeededQuality = useRef(false);
  // Firebase hooks for real-time data syncing
  const {
    data: customers,
    add: addCustomer,
    update: updateCustomer,
    remove: removeCustomer
  } = useFirestoreCollection<Customer>('customers');
  const {
    data: tasks,
    add: addTask,
    update: updateTask,
    remove: removeTask
  } = useFirestoreCollection<Task>('tasks');
  const {
    data: invoices,
    add: addInvoice,
    update: updateInvoice,
    remove: removeInvoice
  } = useFirestoreCollection<Invoice>('invoices');
  const {
    data: payouts,
    add: addPayout,
    update: updatePayout,
    remove: removePayout
  } = useFirestoreCollection<Payout>('payouts');
  const {
    data: earnings,
    add: addEarning,
    update: updateEarning,
    remove: removeEarning
  } = useFirestoreCollection<Earning>('earnings');
  const {
    data: reports,
    add: addReport,
    update: updateReport,
    remove: removeReport
  } = useFirestoreCollection<ReportData>('reports');
  const {
    data: qualityReports,
    loading: qualityLoading,
    add: addQualityReport,
    update: updateQualityReport,
    remove: removeQualityReport
  } = useFirestoreCollection<QualityReport>('qualityReports');

  // Seed the example reports once, so the section is not empty on first visit.
  useEffect(() => {
    if (qualityLoading || hasSeededQuality.current) return;
    hasSeededQuality.current = true;
    if (qualityReports.length === 0) {
      initialQualityReports.forEach(({
        id,
        ...report
      }) => {
        addQualityReport(report as Omit<QualityReport, 'id'>).catch(() => {
          /* seeding is best effort */
        });
      });
      return;
    }

    // Bring reports created before the rename onto the current company name.
    qualityReports.filter(report => report.company?.name !== company.name).forEach(report => {
      if (!report.id) return;
      updateQualityReport(report.id, {
        company: {
          ...company
        }
      } as Partial<QualityReport>).catch(() => {
        /* best effort */
      });
    });
  }, [qualityLoading, qualityReports, addQualityReport, updateQualityReport]);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = localStorage.getItem('isAuthenticated');
      const storedUsername = localStorage.getItem('username');
      if (authStatus === 'true' && storedUsername) {
        setIsAuthenticated(true);
        setUsername(storedUsername);
        try {
          const usersCollection = collection(db, 'users');
          const usersSnapshot = await getDocs(usersCollection);
          const users: any[] = [];
          usersSnapshot.forEach(doc => {
            users.push({
              id: doc.id,
              ...doc.data()
            });
          });
          const user = users.find(u => u.email === storedUsername);
          if (user) {
            setUserRole(user.role);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
    };
    checkAuth();
  }, []);
  const handleLogin = (user: string, role: UserRole) => {
    setUsername(user);
    setUserRole(role);
    setIsAuthenticated(true);
    setCurrentView('dashboard');
  };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setCurrentView('landing');
  };
  const handleChangePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    const storedPassword = localStorage.getItem('password');
    if (currentPassword !== storedPassword) {
      return false;
    }
    localStorage.setItem('password', newPassword);
    return true;
  };
  const getNextTaskId = () => {
    if (tasks.length === 0) return 'O001';
    const taskNumbers = tasks.map(t => parseInt(t.taskId?.substring(1) || '0')).filter(num => !isNaN(num));
    if (taskNumbers.length === 0) return 'O001';
    const maxNumber = Math.max(...taskNumbers);
    const nextNumber = maxNumber + 1;
    if (nextNumber > 999) {
      alert('Maksimalt antal opgaver nået (O999). Kontakt support.');
      return `O${nextNumber}`;
    }
    return `O${nextNumber.toString().padStart(3, '0')}`;
  };
  const getNextCustomerId = () => {
    if (customers.length === 0) return 'K001';
    const customerNumbers = customers.map(c => parseInt(c.customerId.substring(1))).filter(num => !isNaN(num));
    if (customerNumbers.length === 0) return 'K001';
    const maxNumber = Math.max(...customerNumbers);
    const nextNumber = maxNumber + 1;
    if (nextNumber > 999) {
      alert('Maksimalt antal kunder nået (K999). Kontakt support.');
      return `K${nextNumber}`;
    }
    return `K${nextNumber.toString().padStart(3, '0')}`;
  };
  const handleAddCustomer = async (customerData: Omit<Customer, 'id'>) => {
    try {
      await addCustomer(customerData);
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Fejl ved tilføjelse af kunde. Tjek konsollen for detaljer.');
    }
  };
  const handleEditCustomer = (customer: Customer) => {
    setCustomerToEdit(customer);
    setIsEditCustomerModalOpen(true);
  };
  const handleSaveCustomer = async (updatedCustomer: Customer) => {
    try {
      if (!updatedCustomer.id) return;
      await updateCustomer(updatedCustomer.id, updatedCustomer);
      const customerTasks = tasks.filter(t => t.customerId === updatedCustomer.id);
      for (const task of customerTasks) {
        if (task.id) {
          await updateTask(task.id, {
            ...task,
            customerName: updatedCustomer.name
          });
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Fejl ved opdatering af kunde. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteCustomer = async (customer: Customer) => {
    const customerTasks = tasks.filter(t => t.customerId === customer.id);
    const confirmMessage = customerTasks.length > 0 ? `Er du sikker på at du vil slette kunden "${customer.name}"?\n\nDenne kunde har ${customerTasks.length} opgave(r) som også vil blive slettet.` : `Er du sikker på at du vil slette kunden "${customer.name}"?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      for (const task of customerTasks) {
        if (task.id) {
          await removeTask(task.id);
        }
      }
      if (customer.id) {
        await removeCustomer(customer.id);
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Fejl ved sletning af kunde. Tjek konsollen for detaljer.');
    }
  };
  const handleAddTask = async (taskData: Omit<Task, 'id' | 'customerName'>) => {
    try {
      const customer = customers.find(c => c.id === taskData.customerId);
      if (!customer) {
        alert('Kunde ikke fundet. Vælg venligst en gyldig kunde.');
        return;
      }
      await addTask({
        ...taskData,
        customerName: customer.name
      });
    } catch (error) {
      console.error('Error adding task:', error);
      alert('Fejl ved tilføjelse af opgave. Tjek konsollen for detaljer.');
    }
  };
  // Opens the add-task page with a pre-selected customer
  const handleOpenAddTaskWithCustomer = (customerId: string) => {
    setInitialCustomerIdForTask(customerId);
    setCurrentView('add-task-page');
  };
  const handleSaveTask = async (updatedTask: Task) => {
    try {
      if (!updatedTask.id) return;
      const cleanTask: any = {};
      Object.keys(updatedTask).forEach(key => {
        const value = (updatedTask as any)[key];
        if (value !== undefined) {
          cleanTask[key] = value;
        }
      });
      await updateTask(updatedTask.id, cleanTask);
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Fejl ved opdatering af opgave. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteTask = async (task: Task) => {
    if (!window.confirm(`Er du sikker på at du vil slette opgaven "${task.title}"?`)) return;
    try {
      if (task.id) {
        await removeTask(task.id);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Fejl ved sletning af opgave. Tjek konsollen for detaljer.');
    }
  };
  const handleTaskStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      await updateTask(taskId, {
        ...task,
        status: newStatus
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Fejl ved opdatering af opgavestatus. Tjek konsollen for detaljer.');
    }
  };
  const handleAddInvoice = async (invoiceData: Omit<Invoice, 'id'>) => {
    try {
      await addInvoice(invoiceData);
    } catch (error) {
      console.error('Error adding invoice:', error);
      alert('Fejl ved tilføjelse af faktura. Tjek konsollen for detaljer.');
    }
  };
  const handleEditInvoice = async (updatedInvoice: Invoice) => {
    try {
      if (!updatedInvoice.id) return;
      await updateInvoice(updatedInvoice.id, updatedInvoice);
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Fejl ved opdatering af faktura. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteInvoice = async (invoice: Invoice) => {
    if (!window.confirm(`Er du sikker på at du vil slette fakturaen fra "${invoice.supplier}"?`)) return;
    try {
      if (invoice.id) {
        await removeInvoice(invoice.id);
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Fejl ved sletning af faktura. Tjek konsollen for detaljer.');
    }
  };
  const handleAddPayout = async (payoutData: Omit<Payout, 'id'>) => {
    try {
      await addPayout(payoutData);
    } catch (error) {
      console.error('Error adding payout:', error);
      alert('Fejl ved tilføjelse af udbetaling. Tjek konsollen for detaljer.');
    }
  };
  const handleEditPayout = async (updatedPayout: Payout) => {
    try {
      if (!updatedPayout.id) return;
      await updatePayout(updatedPayout.id, updatedPayout);
    } catch (error) {
      console.error('Error updating payout:', error);
      alert('Fejl ved opdatering af udbetaling. Tjek konsollen for detaljer.');
    }
  };
  const handleDeletePayout = async (payout: Payout) => {
    if (!window.confirm(`Er du sikker på at du vil slette udbetalingen på ${payout.amount.toLocaleString('da-DK')} kr?`)) return;
    try {
      if (payout.id) {
        await removePayout(payout.id);
      }
    } catch (error) {
      console.error('Error deleting payout:', error);
      alert('Fejl ved sletning af udbetaling. Tjek konsollen for detaljer.');
    }
  };
  const handleAddEarning = async (earningData: Omit<Earning, 'id'>) => {
    try {
      await addEarning(earningData);
    } catch (error) {
      console.error('Error adding earning:', error);
      alert('Fejl ved tilføjelse af indtjening. Tjek konsollen for detaljer.');
    }
  };
  const handleEditEarning = async (updatedEarning: Earning) => {
    try {
      if (!updatedEarning.id) return;
      await updateEarning(updatedEarning.id, updatedEarning);
    } catch (error) {
      console.error('Error updating earning:', error);
      alert('Fejl ved opdatering af indtjening. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteEarning = async (earning: Earning) => {
    if (!window.confirm(`Er du sikker på at du vil slette indtjeningen på ${earning.amount.toLocaleString('da-DK')} kr?`)) return;
    try {
      if (earning.id) {
        await removeEarning(earning.id);
      }
    } catch (error) {
      console.error('Error deleting earning:', error);
      alert('Fejl ved sletning af indtjening. Tjek konsollen for detaljer.');
    }
  };
  const handleAddReport = async (reportData: Omit<ReportData, 'id' | 'createdAt'>) => {
    try {
      await addReport(reportData as any);
      alert(`Rapport "${reportData.title}" er oprettet og gemt i Firebase!`);
    } catch (error) {
      console.error('Error adding report:', error);
      alert('Fejl ved tilføjelse af rapport. Tjek konsollen for detaljer.');
    }
  };
  const handleUpdateReport = async (reportData: ReportData) => {
    try {
      if (!reportData.id) return;
      await updateReport(reportData.id, reportData as any);
      alert(`Rapport "${reportData.title}" er opdateret!`);
    } catch (error) {
      console.error('Error updating report:', error);
      alert('Fejl ved opdatering af rapport. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteReport = async (reportId: string) => {
    try {
      await removeReport(reportId);
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Fejl ved sletning af rapport. Tjek konsollen for detaljer.');
    }
  };
  const handleViewCustomerDetail = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setCurrentView('customer-detail');
  };
  const handleBackFromDetail = () => {
    setSelectedCustomerId(null);
    setCurrentView('customers');
  };
  const handleViewTaskDetail = (taskId: string) => {
    setPreviousView(currentView);
    setSelectedTaskId(taskId);
    setCurrentView('task-detail');
  };
  const handleBackFromTaskDetail = () => {
    setSelectedTaskId(null);
    setCurrentView(previousView === 'task-detail' ? 'tasks' : previousView);
  };
  const isAdmin = userRole === 'Admin';
  const isReadOnly = userRole === 'Read-Only';
  const selectedCustomer = selectedCustomerId ? customers.find(c => c.id === selectedCustomerId) : null;
  const selectedTask = selectedTaskId ? tasks.find(t => t.id === selectedTaskId) : null;
  const selectedQualityReport = selectedQualityReportId ? qualityReports.find(r => r.id === selectedQualityReportId) : null;
  const handleOpenQualityReport = (reportId: string) => {
    setSelectedQualityReportId(reportId);
    setCurrentView('quality-detail');
  };
  const handleSaveQualityReport = async (updated: QualityReport) => {
    try {
      if (!updated.id) return;
      const {
        id,
        ...fields
      } = updated;
      await updateQualityReport(id, fields as Partial<QualityReport>);
    } catch (error) {
      console.error('Error saving quality report:', error);
      alert('Fejl ved gemning af kvalitetssikring.');
    }
  };
  const getNextQualityReportId = () => {
    const year = new Date().getFullYear();
    const numbers = qualityReports.map(r => parseInt(r.reportId?.split('-').pop() || '0', 10)).filter(n => !Number.isNaN(n));
    const next = numbers.length === 0 ? 1 : Math.max(...numbers) + 1;
    return `KS-${year}-${next.toString().padStart(4, '0')}`;
  };
  const handleCreateQualityReport = async (report: Omit<QualityReport, 'id'>) => {
    try {
      const newId = await addQualityReport(report);
      if (newId) {
        setSelectedQualityReportId(newId);
        setCurrentView('quality-detail');
      }
    } catch (error) {
      console.error('Error creating quality report:', error);
      alert('Fejl ved oprettelse af kvalitetssikring.');
    }
  };
  const handleDeleteQualityReport = async (report: QualityReport) => {
    if (!window.confirm(`Er du sikker på at du vil slette kvalitetssikringen "${report.title}"?`)) return;
    try {
      if (report.id) await removeQualityReport(report.id);
    } catch (error) {
      console.error('Error deleting quality report:', error);
      alert('Fejl ved sletning af kvalitetssikring.');
    }
  };

  /** Global search jumps straight to the matched record. */
  const handleSearchResult = (result: SearchResult) => {
    if (result.view === 'customer-detail' && result.recordId) {
      handleViewCustomerDetail(result.recordId);
      return;
    }
    if (result.view === 'task-detail' && result.recordId) {
      handleViewTaskDetail(result.recordId);
      return;
    }
    if (result.view === 'quality-detail' && result.recordId) {
      handleOpenQualityReport(result.recordId);
      return;
    }
    setCurrentView(result.view);
  };
  if (!isAuthenticated && currentView === 'landing') {
    return <LandingPage onLoginClick={() => setCurrentView('login')} data-id="element-0" data-mp="8xl3clqx17vy" />;
  }
  if (!isAuthenticated && currentView === 'login') {
    return <Login onLogin={handleLogin} data-id="element-1" data-mp="3woqvjsi0dex" />;
  }
  return <div className="min-h-screen w-full bg-stone-50 lg:flex" data-id="element-2" data-mp="3w1r6v82cwrvd">
      <Sidebar currentView={currentView} onNavigate={view => {
      setCurrentView(view);
      setIsMobileNavOpen(false);
    }} isAdmin={isAdmin} username={username} userRole={userRole} onLogout={handleLogout} isMobileOpen={isMobileNavOpen} onMobileClose={() => setIsMobileNavOpen(false)} data-id="element-3" data-mp="3r3pm8ipa8khs" />

      <div className="min-w-0 flex-1" data-id="element-4" data-mp="1kb6q609ayxeb">
        <TopBar customers={customers} tasks={tasks} qualityReports={qualityReports} isAdmin={isAdmin} onSelectResult={handleSearchResult} data-id="element-5" data-mp="2c7vtteo964h3" />

        <main className="mx-auto max-w-[1600px] px-4 pb-28 pt-6 sm:px-6 sm:pt-8 lg:px-10 lg:pb-8" data-id="element-6" data-mp="22h16s836n0ul">
          {currentView === 'dashboard' && <Dashboard customers={customers} tasks={tasks} invoices={invoices} earnings={earnings} payouts={payouts} onAddTask={handleAddTask} onEditTask={task => handleViewTaskDetail(task.id!)} onViewCustomer={handleViewCustomerDetail} data-id="element-7" data-mp="1xi6bw7rxxlu4" />}
          {currentView === 'customers' && <CustomerManagement customers={customers} tasks={tasks} onOpenCustomerModal={() => !isReadOnly && setIsCustomerModalOpen(true)} onOpenTaskModal={() => !isReadOnly && setCurrentView('add-task-page')} onTaskStatusChange={handleTaskStatusChange} onEditCustomer={customer => !isReadOnly && handleEditCustomer(customer)} onEditTask={task => !isReadOnly && handleViewTaskDetail(task.id!)} onDeleteCustomer={customer => !isReadOnly && handleDeleteCustomer(customer)} onDeleteTask={task => !isReadOnly && handleDeleteTask(task)} onViewCustomerDetail={handleViewCustomerDetail} viewMode="customers" data-id="element-8" data-mp="1elybbh9vhz55" />}
          {currentView === 'tasks' && <CustomerManagement customers={customers} tasks={tasks} onOpenCustomerModal={() => !isReadOnly && setIsCustomerModalOpen(true)} onOpenTaskModal={() => !isReadOnly && setCurrentView('add-task-page')} onTaskStatusChange={handleTaskStatusChange} onEditCustomer={customer => !isReadOnly && handleEditCustomer(customer)} onEditTask={task => !isReadOnly && handleViewTaskDetail(task.id!)} onDeleteCustomer={customer => !isReadOnly && handleDeleteCustomer(customer)} onDeleteTask={task => !isReadOnly && handleDeleteTask(task)} onViewCustomerDetail={handleViewCustomerDetail} viewMode="tasks" data-id="element-9" data-mp="3cdg0o9p2jnyq" />}
          {currentView === 'task-detail' && selectedTask && <TaskDetail task={selectedTask} customers={customers} onBack={handleBackFromTaskDetail} onSave={handleSaveTask} isReadOnly={isReadOnly} data-id="element-10" data-mp="3kdawe1dukk5b" />}
          {currentView === 'report' && <Report tasks={tasks} customers={customers} isReadOnly={isReadOnly} reports={reports} onAddReport={handleAddReport} onUpdateReport={handleUpdateReport} onDeleteReport={handleDeleteReport} data-id="element-11" data-mp="1774kff5wlmc4" />}
          {currentView === 'calendar' && <Calendar tasks={tasks} customers={customers} data-id="element-12" data-mp="1pzyq1ihn023o" />}
          {currentView === 'invoices' && <Invoices invoices={invoices} onAddInvoice={handleAddInvoice} onEditInvoice={handleEditInvoice} onDeleteInvoice={handleDeleteInvoice} canAddInvoice={isAdmin} customers={customers} data-id="element-13" data-mp="1uxi2qdyos6ti" />}
          {currentView === 'payouts' && isAdmin && <Payouts payouts={payouts} earnings={earnings} onAddPayout={handleAddPayout} onEditPayout={handleEditPayout} onDeletePayout={handleDeletePayout} onAddEarning={handleAddEarning} onEditEarning={handleEditEarning} onDeleteEarning={handleDeleteEarning} customers={customers} data-id="element-14" data-mp="9l6vayx0qir7" />}
          {currentView === 'mester-ai' && <MesterAI data-id="element-15" data-mp="b0ui0lyak6np" />}
          {currentView === 'quality' && <QualityAssurance reports={qualityReports} customers={customers} onOpenReport={handleOpenQualityReport} onCreateReport={handleCreateQualityReport} onDeleteReport={handleDeleteQualityReport} nextReportId={getNextQualityReportId()} responsible={{
          name: username,
          email: username,
          phone: ''
        }} canEdit={!isReadOnly} data-id="element-16" data-mp="18pcyuydrg9sn" />}
          {currentView === 'quality-detail' && selectedQualityReport && <QualityReportDetail report={selectedQualityReport} onBack={() => {
          setSelectedQualityReportId(null);
          setCurrentView('quality');
        }} onChange={handleSaveQualityReport} data-id="element-17" data-mp="3fji1szx5kdnf" />}
          {currentView === 'solar' && <Solar data-id="element-18" data-mp="28q7zymgdqec9" />}
          {currentView === 'regelgrundlag' && <Regelgrundlag data-id="element-19" data-mp="28rstq9uvdt92" />}
          {currentView === 'profile' && <Profile username={username} onChangePassword={handleChangePassword} data-id="element-20" data-mp="2doj9sx6ocouh" />}
          {currentView === 'users' && isAdmin && <Users currentUserEmail={username} data-id="element-21" data-mp="2mq0xp1qbputq" />}
          {currentView === 'charger-settings' && isAdmin && <ChargerSettings data-id="element-22" data-mp="2roat9dfymxfv" />}
          {currentView === 'add-task-page' && isAdmin && <AddTaskPage customers={customers} nextTaskId={getNextTaskId()} onAdd={task => {
          handleAddTask(task);
          const returnView: View = initialCustomerIdForTask ? 'customer-detail' : 'manage-tasks-page';
          setInitialCustomerIdForTask(undefined);
          setCurrentView(returnView);
        }} onBack={() => {
          const returnView: View = initialCustomerIdForTask ? 'customer-detail' : 'tasks';
          setInitialCustomerIdForTask(undefined);
          setCurrentView(returnView);
        }} initialCustomerId={initialCustomerIdForTask} data-id="element-23" data-mp="sd1dswewuegn" />}
          {currentView === 'manage-tasks-page' && isAdmin && <ManageTasksPage tasks={tasks} customers={customers} onEditTask={task => handleViewTaskDetail(task.id!)} onDeleteTask={handleDeleteTask} data-id="element-24" data-mp="2sf90mnmhwil1" />}
          {currentView === 'customer-detail' && selectedCustomer && <CustomerDetail customer={selectedCustomer} tasks={tasks} onBack={handleBackFromDetail} onEdit={customer => !isReadOnly && handleEditCustomer(customer)} onEditTask={task => handleViewTaskDetail(task.id!)} onDeleteTask={handleDeleteTask} onAddTask={handleOpenAddTaskWithCustomer} data-id="element-25" data-mp="2xaoe3eglccgx" />}
        </main>
      </div>

      <BottomNav currentView={currentView} onNavigate={view => {
      setIsMobileNavOpen(false);
      setCurrentView(view);
    }} onOpenMore={() => setIsMobileNavOpen(!isMobileNavOpen)} isMoreOpen={isMobileNavOpen} data-id="element-26" data-mp="2y6rze4wc33vr" />

      {!isReadOnly && <>
          <AddCustomerModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} onAdd={handleAddCustomer} nextCustomerId={getNextCustomerId()} data-id="element-27" data-mp="2t9i3c6efvtyy" />
          <EditCustomerModal isOpen={isEditCustomerModalOpen} onClose={() => {
        setIsEditCustomerModalOpen(false);
        setCustomerToEdit(null);
      }} onSave={handleSaveCustomer} customer={customerToEdit} data-id="element-28" data-mp="2jcn41lacdpjm" />
        </>}
    </div>;
}